import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { buildWaiter, waitFor } from '@ember/test-waiters';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pipe } from '@nullvoxpopuli/ember-composable-helpers';
import { endOfDay, formatISO, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { dropTask, enqueueTask, restartableTask, timeout } from 'ember-concurrency';
import task from 'ember-concurrency/helpers/task';
import { pluralize } from 'ember-inflector';
import RouteTemplate from 'ember-route-template';
import EmberWormhole from 'ember-wormhole/components/ember-wormhole';
import DateRanger from 'garaje/components/date-ranger';
import HeaderSelector from 'garaje/components/header-selector';
import SetupGuideSteps from 'garaje/components/setup-guide-steps';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import UiHeader from 'garaje/components/ui/header';
import UiPage from 'garaje/components/ui/page';
import UiInput from 'garaje/components/ui-input';
import VisitorsApprovalsTable from 'garaje/components/visitors/approvals-table';
import VisitorsApprovalsTableData from 'garaje/components/visitors/approvals-table/data';
import type { ReportSource } from 'garaje/models/approval-status';
import type { InviteWithVisitApproval } from 'garaje/models/invite';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type CurrentAdminService from 'garaje/services/current-admin';
import type CurrentLocationService from 'garaje/services/current-location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { approvalMessage } from 'garaje/utils/approval-message';
import { parseYyyyMmDdInTimeZone } from 'garaje/utils/date-fns-tz-utilities';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { fetchCapacity } from 'garaje/utils/locations-capacity';
import { or, reads } from 'macro-decorators';

import type VisitorsApprovalsIndexController from './controller';
import type { VisitorsApprovalsIndexModel } from './route';

type DateRangeFilter = { from: string; to: string };
export type ApprovalsFilters = { [key: string]: string | DateRangeFilter };

interface VisitorsApprovalsIndexRouteTemplateSignature {
  Args: {
    model: VisitorsApprovalsIndexModel;
    controller: VisitorsApprovalsIndexController;
  };
}

const bulkApproveInvitesWaiter = buildWaiter('visitors-approvals-index-controller:bulkApproveInvites');
const bulkDenyInvitesWaiter = buildWaiter('visitors-approvals-index-controller:bulkDenyInvites');

const QUERY_PARAM_DATE_FORMAT = 'yyyy-MM-dd';

const DEBOUNCE_TIMEOUT = 300; // time in ms for debouncing 'query' field

const DATE_RANGE_SHORTCUTS = [
  'today',
  'next2Days',
  'thisWeek',
  'next7Days',
  'next30Days',
  'thisMonth',
  'past7Days',
  'past30Days',
  'past90Days',
  'monthToDate',
  'yearToDate',
  'pastYear',
  'allTime',
];

class VisitorsApprovalsIndexRouteTemplate extends Component<VisitorsApprovalsIndexRouteTemplateSignature> {
  @service declare ajaxFetch: AjaxFetchService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare currentLocation: CurrentLocationService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;
  @service declare store: StoreService;

  dateRangeFilterOptions = DATE_RANGE_SHORTCUTS;

  @or('currentAdmin.isGlobalAdmin', 'currentAdmin.isLocationAdmin') isAdmin!: boolean;
  @reads('currentLocation.location.timezone', 'America/Los_Angeles') timezone!: string;

  @tracked search = '';

  get showSetupGuide(): boolean {
    return this.isAdmin && this.featureFlags.isEnabled('growth_show_visitors_setup_guide_stepper');
  }

  /**
   * Helper to extract the workflowId from an invite's screener results
   */
  getWorkflowId(invite: InviteWithVisitApproval): string | null {
    if (!this.featureFlags.isEnabled('visit-workflows-approval-chain-m1')) {
      return null;
    }

    const resultWithWorkflow = invite.visitApproval.screenerResults.find((result) => result.workflowId !== null);
    return resultWithWorkflow?.workflowId ?? null;
  }

  /**
   * Helper to notify the workflow system of an approval/denial decision
   */
  async notifyWorkflow(workflowId: string, approved: boolean, reason?: string): Promise<boolean> {
    if (!this.featureFlags.isEnabled('visit-workflows-approval-chain-m1')) {
      return false;
    }

    try {
      await this.ajaxFetch.request(`/a/visits/api/v1/approvals/workflowTask/${workflowId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          approved,
          reason: reason || null,
        },
      });
      return true;
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Error notifying workflow system', {
        details: parseErrorForDisplay(error),
      });
      return false;
    }
  }
  get columns(): string[] {
    return this.args.model.columnOptions.filter((column) => column.show).map((column) => column.value);
  }

  get filters(): ApprovalsFilters {
    const filters: ApprovalsFilters = {};
    if (this.state.currentLocation?.id) {
      filters['location'] = this.state.currentLocation.id;
    }
    if (this.args.controller.query) {
      filters['query'] = this.args.controller.query;
    }
    filters['arrival-time'] = {
      from: formatISO(startOfDay(this.startDate)),
      to: formatISO(endOfDay(this.endDate)),
    };

    return filters;
  }

  get endDate(): Date {
    // return Date object based on the string from the query param
    const date = this.args.controller.end
      ? parseYyyyMmDdInTimeZone(this.args.controller.end, new Date(), this.timezone)
      : new Date();
    return date;
  }

  get startDate(): Date {
    // return Date object based on the string from the query param
    const date = this.args.controller.start
      ? parseYyyyMmDdInTimeZone(this.args.controller.start, new Date(), this.timezone)
      : new Date();
    return date;
  }

  @action
  selectDateRange(reload: () => void, startDate: Date, endDate: Date): void {
    const newStart = formatInTimeZone(startDate, this.timezone, QUERY_PARAM_DATE_FORMAT);
    const newEnd = formatInTimeZone(endDate, this.timezone, QUERY_PARAM_DATE_FORMAT);

    const rangeChanged = newStart !== this.args.controller.start || newEnd !== this.args.controller.end;

    this.args.controller.start = newStart;
    this.args.controller.end = newEnd;

    if (rangeChanged) reload();
  }

  @action
  setSort(field: string, direction: 'asc' | 'desc'): void {
    this.args.controller.sortBy = field;
    this.args.controller.sortDirection = direction;
  }

  approveInviteTask = enqueueTask(
    waitFor(
      async (
        removeInvitesFromView: (invites: InviteWithVisitApproval[]) => void,
        invite: InviteWithVisitApproval,
        reason?: string,
      ): Promise<void> => {
        try {
          // Notify workflow system if this invite has a workflow
          const workflowId = this.getWorkflowId(invite);
          if (workflowId) {
            await this.notifyWorkflow(workflowId, true, reason);
            this.flashMessages.showAndHideFlash('success', 'Visitor approved');
            this.metrics.trackEvent('Dashboard Invite - Reviewed', {
              action: 'approve',
              invite_id: invite.id,
              source: 'Approvals page',
              workflow: true,
            });
            removeInvitesFromView([invite]);
            return;
          }

          // only review screener sources that the user has access to review from the visit approval
          await invite.approve(invite.visitApproval.reviewableScreenerSources);

          let additionalFlashMessageText;
          if (!invite.preregistrationComplete) {
            if (invite.approved) {
              additionalFlashMessageText = 'Invitation sent!';
            } else if (invite.needsPropertyApprovalReview) {
              // only flash this message  if a report with a property based source is present
              additionalFlashMessageText =
                'Additional approval from the property manager is required before the invitation is sent.';
            }
          }

          additionalFlashMessageText = approvalMessage(invite) ?? additionalFlashMessageText;

          this.flashMessages.showAndHideFlash('success', 'Visitor approved', { details: additionalFlashMessageText });
          const sourcesForApprovalReviewMetrics = (invite.visitApproval?.failedResults || []).reduce(
            (sources, result) => ({ ...sources, [result.screenerSource]: true }),
            {},
          );
          this.metrics.trackEvent('Dashboard Invite - Reviewed', {
            action: 'approve',
            invite_id: invite.id,
            source: 'Approvals page',
            ...sourcesForApprovalReviewMetrics,
          });

          // invite could still have other screeners that need to be reviewed
          if (invite.approved) removeInvitesFromView([invite]);
        } catch (error) {
          this.flashMessages.showAndHideFlash('error', 'Error approving invite', parseErrorForDisplay(error));
        } finally {
          const currentLocation = this.currentLocation.location;
          if (currentLocation.capacityLimitEnabled) {
            await fetchCapacity(this.store, currentLocation, invite.expectedArrivalTime);
          }
        }
      },
    ),
  );

  denyInviteTask = enqueueTask(
    waitFor(
      async (
        removeInvitesFromView: (invites: InviteWithVisitApproval[]) => void,
        invite: InviteWithVisitApproval,
        reason?: string,
      ): Promise<void> => {
        try {
          // Notify workflow system if this invite has a workflow
          const workflowId = this.getWorkflowId(invite);
          if (workflowId) {
            await this.notifyWorkflow(workflowId, false, reason);
            this.flashMessages.showAndHideFlash('success', 'Visitor denied');
            this.metrics.trackEvent('Dashboard Invite - Reviewed', {
              action: 'deny',
              invite_id: invite.id,
              source: 'Approvals page',
              workflow: true,
            });
            removeInvitesFromView([invite]);
            return;
          }

          await invite.deny();
          this.flashMessages.showAndHideFlash('success', 'Visitor denied');

          removeInvitesFromView([invite]);
        } catch (error) {
          this.flashMessages.showAndHideFlash('error', 'Error denying invite', {
            details: parseErrorForDisplay(error),
          });
        }
      },
    ),
  );

  bulkApproveInvitesTask = dropTask(
    async (
      removeInvitesFromView: (invites: InviteWithVisitApproval[]) => void,
      invites: InviteWithVisitApproval[],
    ): Promise<InviteWithVisitApproval[]> => {
      const token = bulkApproveInvitesWaiter.beginAsync();
      const approvedInvites: InviteWithVisitApproval[] = [];
      const failedInvites: InviteWithVisitApproval[] = [];
      let failedInviteCount = 0;

      for (const invite of invites) {
        try {
          // Notify workflow system if this invite has a workflow
          const workflowId = this.getWorkflowId(invite);
          if (workflowId) {
            const success = await this.notifyWorkflow(workflowId, true);
            if (!success) {
              failedInvites.push(invite);
              failedInviteCount++;
            } else {
              approvedInvites.push(invite);
            }

            continue;
          }

          // only review screener sources that the user has access to review from the visit approval
          const screeners: ReportSource[] | undefined = invite.visitApproval.reviewableScreenerSources;
          await invite.approve(screeners);

          // invite could still have other screeners that need to be reviewed
          if (invite.approved) approvedInvites.push(invite);
        } catch (_error) {
          failedInvites.push(invite);
          failedInviteCount++;
        }
      }

      removeInvitesFromView(approvedInvites);

      if (approvedInvites.length === invites.length) {
        // all invites approved successfully
        this.flashMessages.showAndHideFlash('success', `${pluralize(approvedInvites.length, 'visitor')} approved`);
      } else if (failedInviteCount > 0 && approvedInvites.length > 0) {
        // some (but not all) invites failed
        this.flashMessages.showAndHideFlash('error', `Approving ${pluralize(failedInviteCount, 'visitor')} failed`, {
          details: `${pluralize(approvedInvites.length, 'visitors')} were approved`,
        });
      } else {
        // all invites failed
        this.flashMessages.showAndHideFlash('error', `Approving ${pluralize(failedInviteCount, 'visitor')} failed`, {
          details: 'Please try again later.',
        });
      }

      bulkApproveInvitesWaiter.endAsync(token);

      return approvedInvites;
    },
  );

  bulkDenyInvitesTask = dropTask(
    async (
      removeInvitesFromView: (invites: InviteWithVisitApproval[]) => void,
      invites: InviteWithVisitApproval[],
    ): Promise<InviteWithVisitApproval[]> => {
      const deniedInvites: InviteWithVisitApproval[] = [];
      const failedInvites: InviteWithVisitApproval[] = [];
      let failedInviteCount = 0;

      const token = bulkDenyInvitesWaiter.beginAsync();

      for (const invite of invites) {
        try {
          // Notify workflow system if this invite has a workflow
          const workflowId = this.getWorkflowId(invite);
          if (workflowId) {
            const success = await this.notifyWorkflow(workflowId, false);
            if (!success) {
              failedInvites.push(invite);
              failedInviteCount++;
            } else {
              deniedInvites.push(invite);
            }
            continue;
          }

          await invite.deny();

          deniedInvites.push(invite);
        } catch (_error) {
          failedInvites.push(invite);
          failedInviteCount++;
        }
      }

      removeInvitesFromView(deniedInvites);

      if (deniedInvites.length === invites.length) {
        // all invites denied successfully
        this.flashMessages.showAndHideFlash('success', `${pluralize(deniedInvites.length, 'visitor')} denied`);
      } else if (failedInviteCount > 0 && deniedInvites.length > 0) {
        // some (but not all) invites failed
        this.flashMessages.showAndHideFlash('error', `Denying ${pluralize(failedInviteCount, 'visitor')} failed`, {
          details: `${pluralize(deniedInvites.length, 'visitors')} were denied`,
        });
      } else {
        // all invites failed
        this.flashMessages.showAndHideFlash('error', `Denying ${pluralize(failedInviteCount, 'visitor')} failed`, {
          details: 'Please try again later.',
        });
      }

      bulkDenyInvitesWaiter.endAsync(token);

      return deniedInvites;
    },
  );

  setQueryTask = restartableTask(async (reloadData: () => void, value: string) => {
    await timeout(DEBOUNCE_TIMEOUT);
    this.args.controller.query = value;
    reloadData();
  });

  <template>
    {{#if this.showSetupGuide}}
      <EmberWormhole @to="onboarding">
        <SetupGuideSteps />
      </EmberWormhole>
    {{/if}}

    <UiPage class="overflow-x-auto">
      <UiHeader>Approvals</UiHeader>

      <VisitorsApprovalsTableData
        @filters={{this.filters}}
        @sortBy={{@controller.sortBy}}
        @sortDirection={{@controller.sortDirection}}
        as |data pagination|
      >
        <div class="flex mb-8">
          <div class="grow">
            <DateRanger
              @startDate={{this.startDate}}
              @endDate={{this.endDate}}
              @onDateSelected={{fn this.selectDateRange data.reload}}
              @onDateRangeSelected={{fn this.selectDateRange data.reload}}
              @timezone={{this.timezone}}
              @shortcuts={{this.dateRangeFilterOptions}}
              data-test-date-range
            />
          </div>

          <div class="grow-0">
            <HeaderSelector @options={{@model.columnOptions}} />
          </div>

          <div class="grow-0 mr-3">
            <UiInput
              data-test-query-filter
              class="pageSearch border p-2 icon-search max-w-full"
              @type="search"
              @placeholder="Search in list"
              @value={{this.search}}
              @update={{fn this.setQueryTask.perform data.reload}}
            />
          </div>
        </div>

        {{#if data.invites}}
          <VisitorsApprovalsTable
            @approveInviteTask={{task this.approveInviteTask data.removeInvites}}
            @bulkApproveInvitesTask={{task this.bulkApproveInvitesTask data.removeInvites}}
            @bulkDenyInvitesTask={{task this.bulkDenyInvitesTask data.removeInvites}}
            @columns={{this.columns}}
            @denyInviteTask={{task this.denyInviteTask data.removeInvites}}
            @hasMoreData={{pagination.hasMorePages}}
            @invites={{data.invites}}
            @changeSort={{pipe this.setSort data.reload}}
            @loadMoreData={{pagination.loadMore}}
            @sortBy={{@controller.sortBy}}
            @sortDirection={{@controller.sortDirection}}
          />
        {{else if data.loading}}
          <LoadingSpinnerPulse data-test-loading-spinner class="pt-12" />
        {{else}}
          <div class="text-center !my-8" data-test-no-approvals>
            <img src="/assets/images/empty-state/no-invites.png" alt="No invites" class="empty-invites" />
            <h2 data-test-empty-invites-list-message class="mt-8 font-normal text-carbon-50">
              No invites
            </h2>
          </div>
        {{/if}}
      </VisitorsApprovalsTableData>
    </UiPage>
  </template>
}

export default RouteTemplate(VisitorsApprovalsIndexRouteTemplate);
